import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Weight Loss / GLP-1': { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  'Nootropic / Cognitive': { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  'Aesthetics / Skin': { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-200' },
  'Anti-aging': { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
  'Recovery': { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  'GH / Secretagogues': { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  'Hormonal / Sexual Health': { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
  'Immunity': { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
  'Bioregulators': { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  'Metabolism': { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  'Diluents': { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

const getColor = (cat) => CATEGORY_COLORS[cat] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };

export default function Library() {
  const [peptides, setPeptides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('peptides');
  const [hubs, setHubs] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasAccess = user && user.has_lifetime_access;

  useEffect(() => {
    fetch(`${API}/api/library`)
      .then(r => r.json())
      .then(data => {
        setPeptides(data.peptides || []);
        setCategories(data.categories || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    fetch(`${API}/api/hubs`)
      .then(r => r.json())
      .then(data => {
        setHubs(data.hubs || []);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = peptides;
    if (activeCategory !== 'All') {
      list = list.filter(p => p.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
      );
    }
    return list;
  }, [peptides, activeCategory, search]);

  const filteredHubs = useMemo(() => {
    let list = hubs;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(h =>
        (h.peptide_name && h.peptide_name.toLowerCase().includes(q)) ||
        (h.title && h.title.toLowerCase().includes(q))
      );
    }
    return list;
  }, [hubs, search]);

  const currentCategories = viewMode === 'peptides' ? categories : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="library-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-white tracking-tight">
              Protocol Library
            </h1>
            <p className="text-base text-blue-100 max-w-2xl mx-auto">
              Complete protocols with dosages, scientific references, and synergies
            </p>
          </div>

          {/* View Mode Toggle - visible to everyone (paywall on detail) */}
          <div className="flex justify-center mb-5">
            <div className="inline-flex bg-white/10 border border-white/20 rounded-xl p-1">
              <button
                data-testid="toggle-peptides"
                onClick={() => { setViewMode('peptides'); setActiveCategory('All'); }}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'peptides' ? 'bg-white text-blue-700 shadow-sm' : 'text-white hover:bg-white/10'}`}
              >
                Peptides ({peptides.length})
              </button>
              <button
                data-testid="toggle-stacks"
                onClick={() => { setViewMode('stacks'); setActiveCategory('All'); }}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'stacks' ? 'bg-white text-blue-700 shadow-sm' : 'text-white hover:bg-white/10'}`}
              >
                Stack Hubs ({hubs.length})
                {!hasAccess && (
                  <span className="ml-1.5 inline-flex items-center bg-yellow-400 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">PRO</span>
                )}
              </button>
            </div>
          </div>

          <p className="text-center text-sm text-blue-200 mb-6">
            {viewMode === 'peptides'
              ? `${peptides.length} peptide protocols available`
              : `${hubs.length} stack hubs · multiple protocols per hub`
            }
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto mb-6">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="library-search"
              placeholder={viewMode === 'peptides' ? "Search peptide by name..." : "Search hub by peptide name..."}
              className="w-full px-5 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
            />
          </div>

          {/* Categories */}
          {viewMode === 'peptides' && (
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${activeCategory === 'All' ? 'bg-white text-blue-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
            >
              All
            </button>
            {currentCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${activeCategory === cat ? 'bg-white text-blue-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
              >
                {cat}
              </button>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
          <p className="text-sm text-blue-700">Scan your product's QR code to unlock protocols. Each product includes access to its complete protocol.</p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {viewMode === 'peptides' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="peptide-grid">
          {filtered.map(pep => {
            const color = getColor(pep.category);
            return (
              <div
                key={pep.slug}
                data-testid={`peptide-card-${pep.slug}`}
                onClick={() => navigate(`/protocols/${pep.slug}`)}
                className={`bg-white border border-gray-200 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-gray-200/60 ${!pep.is_free ? 'relative overflow-hidden' : ''}`}
              >
                {!pep.is_free && <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-50 to-transparent rounded-bl-full" />}
                <div className="flex items-center justify-between mb-3">
                  <span className={`${color.bg} ${color.text} border ${color.border} text-xs font-medium px-2 py-0.5 rounded-full`}>{pep.category}</span>
                  {pep.is_free ? (
                    <span className="bg-green-50 text-green-600 border border-green-200 text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                      FREE
                    </span>
                  ) : (
                    <span className="bg-blue-50 text-blue-600 border border-blue-200 text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                      QR Access
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{pep.name}</h3>
                <p className="text-gray-500 text-sm mb-3 line-clamp-2">{pep.description}</p>
                <div className="text-xs text-gray-400">
                  {pep.presentations && pep.presentations.length > 0 && (
                    <span>Vial: {pep.presentations.join(', ')}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hubs-grid">
          {filteredHubs.map(hub => (
            <div
              key={hub.slug}
              data-testid={`hub-card-${hub.slug}`}
              onClick={() => navigate(`/stacks/${hub.slug}`)}
              className="bg-white border border-gray-200 rounded-xl cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-200/60 relative overflow-hidden group"
            >
              {/* Hero image with overlay */}
              <div className="relative h-32 overflow-hidden bg-gradient-to-br from-purple-900 to-blue-900">
                <img
                  src={`${API}/api/hubs/hero-image/${hub.peptide_slug || hub.slug}`}
                  alt={hub.peptide_name || hub.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                {!hasAccess && (
                  <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1 border border-white/20">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg>
                    Premium
                  </div>
                )}
                <div className="absolute bottom-2 left-3 text-white">
                  <h3 className="text-base font-bold drop-shadow-lg">{hub.peptide_name || hub.title}</h3>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {hub.protocols_count} Protocols
                  </span>
                  {!hasAccess && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0 0v3m0-3h3m-3 0H9m12-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Locked
                    </span>
                  )}
                </div>
                <p className="text-xs text-purple-600 font-medium mb-1 uppercase tracking-wider">{hub.subtitle}</p>
                <p className="text-gray-500 text-sm line-clamp-2">{hub.description}</p>
              </div>
            </div>
          ))}
        </div>
        )}
        {((viewMode === 'peptides' && filtered.length === 0) || (viewMode === 'stacks' && filteredHubs.length === 0)) && (
          <div className="text-center py-12 text-gray-500">
            <p>No {viewMode === 'peptides' ? 'peptides' : 'stack hubs'} found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
}
